import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import interWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import spaceGrotesk700 from "@fontsource/space-grotesk/files/space-grotesk-latin-700-normal.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { AddedToCartToast } from "@/components/cart/AddedToCartToast";
import { installClientErrorTracker, reportClientError, setTrackerUser } from "@/lib/client-errors/tracker";
import { initReferralTracker } from "@/lib/referral/referral-cookie";
import { installAnalytics, setAnalyticsToken } from "@/lib/analytics/tracker";

import { supabase } from "@/integrations/supabase/client";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";

const CartSyncBridge = lazy(() =>
  import("@/components/cart/CartSyncBridge").then((m) => ({ default: m.CartSyncBridge })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    reportClientError(error, { source: "render", action: "root_error_boundary" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#172554" },
      { name: "format-detection", content: "telephone=no" },
      { name: "google-site-verification", content: "Ikl1Oq1RqixrzHUX52v0KegLWwCuG_V5KmxUs72UCk4" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "FIXHEN" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "FIXHEN — Classic Mercedes-Benz & BMW Parts Worldwide" },
      { name: "description", content: "OEM and quality-matched parts for classic Mercedes-Benz (R129, W124, W201, W208) and BMW (E30–E46). Free EU shipping over €99." },
      // og:title / og:description are intentionally set per-route so each
      // leaf page ships a unique social preview. og:image is set per-route
      // for the same reason (a root og:image would override every child).
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon.png" },
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "dns-prefetch", href: "https://djmrfsxtncgwuaxtgvae.supabase.co" },
      { rel: "preconnect", href: "https://djmrfsxtncgwuaxtgvae.supabase.co", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: interWoff2, crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: spaceGrotesk700, crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "FIXHEN",
          url: "https://fixhen.com",
          logo: {
            "@type": "ImageObject",
            url: "https://fixhen.com/logo.png",
            width: 192,
            height: 192,
          },
          email: "info@contact.fixhen.com",
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const [nonCriticalReady, setNonCriticalReady] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    let started = false;
    let timeoutId: number | undefined;

    const startNonCriticalTracking = () => {
      if (cancelled || started) return;
      started = true;
      setNonCriticalReady(true);
      installClientErrorTracker();
      installAnalytics();
      initReferralTracker();

      // sync user identity for logs after the initial product paint path
      supabase.auth.getSession().then(({ data }) => {
        if (cancelled) return;
        setTrackerUser(data.session?.user?.id ?? null, data.session?.user?.email ?? null);
        setAnalyticsToken(data.session?.access_token ?? null);
      }).catch(() => {});
      const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED" || event === "INITIAL_SESSION" || event === "TOKEN_REFRESHED") {
          setTrackerUser(session?.user?.id ?? null, session?.user?.email ?? null);
          setAnalyticsToken(session?.access_token ?? null);
        }
      });

      unsubscribe = () => sub.subscription.unsubscribe();
    };

    timeoutId = window.setTimeout(startNonCriticalTracking, 2600);
    return () => {
      cancelled = true;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      unsubscribe?.();
    };
  }, []);


  return (
    <QueryClientProvider client={queryClient}>
      <GlobalErrorBoundary>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        <CartDrawer />
        {nonCriticalReady ? (
          <Suspense fallback={null}>
            <CartSyncBridge />
          </Suspense>
        ) : null}
        <AddedToCartToast />
        <Toaster
          position="bottom-right"
          richColors
          closeButton
          offset={16}
          mobileOffset={{ top: 16, bottom: 112, left: 12, right: 12 }}
        />
      </GlobalErrorBoundary>
    </QueryClientProvider>
  );
}
